import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import * as Sentry from "@sentry/react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Wand2, Package, Trash2, Calendar, Globe, Camera } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface SavedConfig {
  id: string;
  name: string;
  config: any;
  type: string;
  region_key: string | null;
  updated_at: string;
}

type ArchiveTab = "brief" | "product" | "photographer";

import { REGION_OPTIONS, getRegionDisplay } from "@/lib/regions";

export default function WizardArchive() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<ArchiveTab>("brief");
  const [configs, setConfigs] = useState<SavedConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<SavedConfig | null>(null);

  // New brief dialog
  const [newBriefOpen, setNewBriefOpen] = useState(false);
  const [newBriefRegion, setNewBriefRegion] = useState<string>("none");

  const loadConfigs = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("wizard_configs")
      .select("id, name, config, type, region_key, updated_at")
      .order("updated_at", { ascending: false, nullsFirst: false });
    if (error) {
      console.error("Failed to load wizard configs:", error);
      Sentry.captureException(error);
    }
    if (data) setConfigs(data as SavedConfig[]);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const filtered = configs.filter((c) => c.type === tab);

  const handleOpen = (item: SavedConfig) => {
    navigate(`/wizard-builder/${item.id}`);
  };

  const handleNew = () => {
    if (tab === "brief") {
      setNewBriefRegion("none");
      setNewBriefOpen(true);
    } else if (tab === "photographer") {
      navigate(`/wizard-builder/new?type=photographer`);
    } else {
      navigate(`/wizard-builder/new?type=${tab}`);
    }
  };

  const handleCreateBrief = () => {
    const regionParam = newBriefRegion === "none" ? "" : `&region=${newBriefRegion}`;
    navigate(`/wizard-builder/new?type=brief${regionParam}`);
    setNewBriefOpen(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { error } = await supabase.from("wizard_configs").delete().eq("id", deleteTarget.id);
    if (error) {
      console.error("Failed to delete wizard config:", error);
      Sentry.captureException(error);
    }
    setDeleteTarget(null);
    loadConfigs();
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Wizard Builder</h1>
          <p className="text-sm text-muted-foreground mt-1">Create and manage JSON configurations for briefs and products</p>
        </div>
        <Button onClick={handleNew}>
          <Plus className="w-4 h-4 mr-2" />
          New {tab === "brief" ? "Brief" : tab === "photographer" ? "Photographer" : "Product"}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-muted rounded-lg p-1 w-fit mb-6">
        <button
          onClick={() => setTab("brief")}
          className={cn(
            "flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors",
            tab === "brief"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          <Wand2 className="w-4 h-4" />
          Brief Wizards
          <span className="ml-1 text-xs text-muted-foreground">
            ({configs.filter((c) => c.type === "brief").length})
          </span>
        </button>
        <button
          onClick={() => setTab("product")}
          className={cn(
            "flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors",
            tab === "product"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          <Package className="w-4 h-4" />
          Product Forms
          <span className="ml-1 text-xs text-muted-foreground">
            ({configs.filter((c) => c.type === "product").length})
          </span>
        </button>
        <button
          onClick={() => setTab("photographer")}
          className={cn(
            "flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors",
            tab === "photographer"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          <Camera className="w-4 h-4" />
          Photographer Styles
          <span className="ml-1 text-xs text-muted-foreground">
            ({configs.filter((c) => c.type === "photographer").length})
          </span>
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="grid gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl p-12 text-center">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            {tab === "brief" ? <Wand2 className="w-5 h-5 text-muted-foreground" /> : tab === "photographer" ? <Camera className="w-5 h-5 text-muted-foreground" /> : <Package className="w-5 h-5 text-muted-foreground" />}
          </div>
          <p className="text-sm font-medium">No {tab === "brief" ? "brief wizards" : tab === "photographer" ? "photographer styles" : "product forms"} yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Click "New {tab === "brief" ? "Brief" : tab === "photographer" ? "Photographer" : "Product"}" to get started
          </p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <Button variant="outline" size="sm" onClick={handleNew}>
              <Plus className="w-3.5 h-3.5 mr-1" />
              Create your first
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map((item) => {
            const region = getRegionDisplay(item.region_key);
            return (
              <button
                key={item.id}
                onClick={() => handleOpen(item)}
                className="group flex items-center gap-4 p-4 rounded-lg border border-border bg-card hover:bg-accent/50 transition-colors text-left w-full"
              >
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                  {tab === "brief" ? <Wand2 className="w-4 h-4 text-muted-foreground" /> : tab === "photographer" ? <Camera className="w-4 h-4 text-muted-foreground" /> : <Package className="w-4 h-4 text-muted-foreground" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{item.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {tab === "brief" && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5 gap-1">
                        <span>{region.emoji}</span>
                        <span>{region.label}</span>
                      </Badge>
                    )}
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Calendar className="w-3 h-3" />
                      {new Date(item.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive shrink-0"
                  onClick={(e) => { e.stopPropagation(); setDeleteTarget(item); }}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </button>
            );
          })}
        </div>
      )}

      {/* Delete Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleteTarget?.type === "brief" ? "Brief" : deleteTarget?.type === "photographer" ? "Photographer" : "Product"}</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteTarget?.name}"? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Brief — Region Selector Dialog */}
      <Dialog open={newBriefOpen} onOpenChange={setNewBriefOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Brief Wizard</DialogTitle>
            <DialogDescription>
              Choose a target region for this brief, or select "Global" for region-free products.
            </DialogDescription>
          </DialogHeader>
          <Select value={newBriefRegion} onValueChange={setNewBriefRegion}>
            <SelectTrigger>
              <SelectValue placeholder="Select region" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                <span className="flex items-center gap-2">
                  <Globe className="w-3.5 h-3.5" />
                  Global (no region)
                </span>
              </SelectItem>
              {REGION_OPTIONS.map((r) => (
                <SelectItem key={r.key} value={r.key}>
                  <span className="flex items-center gap-2">
                    <span>{r.emoji}</span>
                    {r.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewBriefOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateBrief}>Create Brief</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
